import React, { ReactNode } from "react";
import { useMobile } from "../../hooks/isMobile.tsx";
import { formatIndexMove, parseIndexLine } from "../../utils/wrapIndexMove.ts";
import { splitBeats } from "../../utils/wrapSplitBeats.ts";

/**
 * Mobile-first FinQ Daily Wrap digest.
 * Market cards + color-coded movers, then scannable filing cards.
 */

const POS = "#059669";
const POS_BG = "#ecfdf5";
const NEG = "#dc2626";
const NEG_BG = "#fef2f2";
const INK = "#0b1220";
const BODY = "#1e293b";
const MUTED = "#64748b";
const SURFACE = "#f8fafc";
const BORDER = "#e2e8f0";
const ACCENT = "#1d4ed8";
const ACCENT_SOFT = "#eff6ff";
const FONT = "Neue Montreal, system-ui, -apple-system, sans-serif";

const stripHtml = (text: string): string =>
  text.replace(/<[^>]*>/g, "").replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");

const stripBold = (text: string): string => text.replace(/\*\*/g, "");

const signOf = (raw: string): "pos" | "neg" | "flat" => {
  const t = raw.replace(/,/g, "").trim();
  if (/^[+]/.test(t) || /\(\+/.test(t)) return "pos";
  if (/^[-−–]/.test(t) || /\([-−–]/.test(t)) return "neg";
  return "flat";
};

const colorForSign = (sign: "pos" | "neg" | "flat") =>
  sign === "pos" ? POS : sign === "neg" ? NEG : BODY;

const bgForSign = (sign: "pos" | "neg" | "flat") =>
  sign === "pos" ? POS_BG : sign === "neg" ? NEG_BG : "#fff";

const renderRich = (text: string): ReactNode[] => {
  const safe = stripHtml(text);
  const nodes: ReactNode[] = [];
  const parts = safe.split(/(\*\*[^*]+\*\*)/g);
  let key = 0;
  for (const part of parts) {
    if (!part) continue;
    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    if (bold) {
      nodes.push(
        <strong key={`b-${key++}`} style={{ fontWeight: 600, color: INK }}>
          {bold[1]}
        </strong>
      );
      continue;
    }
    nodes.push(<React.Fragment key={`t-${key++}`}>{part}</React.Fragment>);
  }
  return nodes;
};

interface Section {
  title: string;
  key: string;
  lines: string[];
}

const sectionKey = (title: string): string => {
  const t = title.toLowerCase();
  if (t.includes("market pulse") || t.includes("market snapshot")) return "market";
  if (t.includes("headline") || t.includes("must know")) return "headlines";
  if (t.includes("result")) return "results";
  if (t.includes("guidance") || t.includes("mgmt") || t.includes("outlook")) return "guidance";
  if (t.includes("capital")) return "capital";
  if (t.includes("order")) return "orders";
  if (t.includes("credit") || t.includes("rating")) return "ratings";
  if (t.includes("calendar")) return "calendar";
  return "other";
};

const parseSections = (content: string): { preamble: string[]; sections: Section[] } => {
  const lines = stripHtml(content || "")
    .replace(/\r\n/g, "\n")
    .split("\n");
  const preamble: string[] = [];
  const sections: Section[] = [];
  let current: Section | null = null;

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (trimmed === "---" || trimmed === "***") {
      if (current) sections.push(current);
      current = { title: "Footer", key: "footer", lines: [] };
      continue;
    }
    const h = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (h) {
      if (current) sections.push(current);
      const title = stripBold(h[2]).trim();
      current = { title, key: sectionKey(title), lines: [] };
      continue;
    }
    if (current) current.lines.push(raw);
    else if (trimmed) preamble.push(raw);
  }
  if (current) sections.push(current);
  return { preamble, sections };
};

const isTableSep = (line: string) => /^\|?[\s:|-]+\|[\s:|-]*\|?$/.test(line.trim());
const splitRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

const parseTickerBullet = (
  line: string
): { ticker: string; rest: string } | null => {
  const t = line.trim().replace(/^[-*]\s+/, "");
  const m = /^\*\*([^*]+)\*\*\s*[—–\-·|:]\s*(.*)$/.exec(t);
  if (m) return { ticker: m[1].trim(), rest: m[2].trim() };
  const m2 = /^([A-Z][A-Z0-9&.-]{1,20})\s*[—–\-·|:]\s*(.*)$/.exec(stripBold(t));
  if (m2) return { ticker: m2[1], rest: m2[2] };
  return null;
};

const parseMoverLine = (line: string) => {
  const b = parseTickerBullet(line);
  if (!b) return null;
  const parts = b.rest
    .split(/[·|]/)
    .map((p) => p.trim().replace(/^(ltp|percentChange)\s*=\s*/i, ""))
    .filter(Boolean);
  return {
    symbol: b.ticker,
    ltp: parts[0] || b.rest,
    pct: parts.find((p) => p.includes("%")) || parts[1],
  };
};

const isPeriodCue = (text: string): boolean =>
  /^(q[1-4]\s*fy|h[12]\s*fy|fy\s*\d|quarter|year ended|months?\s+ended)/i.test(text.trim());

/** Parse a Results bullet body into period + metric rows for clean cards. */
const parseEarningsBeats = (
  text: string
): { period?: string; rows: { label: string; value: string; growth?: string }[] } => {
  const cleaned = text.replace(/\s+/g, " ").trim();
  const chunks = cleaned
    .split(/\s*;\s*/)
    .map((c) => c.trim())
    .filter(Boolean);

  let period: string | undefined;
  const rows: { label: string; value: string; growth?: string }[] = [];

  for (const chunk of chunks) {
    if (!period && isPeriodCue(chunk) && chunk.length < 40) {
      period = chunk.replace(/;+\s*$/, "");
      continue;
    }

    const growthMatch = chunk.match(/\(([^)]*%[^)]*)\)\s*$/);
    const growth = growthMatch?.[1]?.trim();
    const head = (growthMatch ? chunk.slice(0, growthMatch.index) : chunk).trim();

    const amountMatch = head.match(
      /((?:₹|Rs\.?|INR|US\$|\$)\s*[\d,]+\.?\d*\s*(?:Cr|Crore|crores?|Mn|Million|Bn|Billion|Lakh|Lakhs)?|[\d,]+\.?\d*\s*(?:Cr|Crore|crores?|Mn|Million|Bn|Billion|Lakh|Lakhs|billion|million))/i
    );

    if (amountMatch && amountMatch.index !== undefined) {
      const label = head.slice(0, amountMatch.index).trim().replace(/[:\-–—]+\s*$/, "") || "Figure";
      const value = amountMatch[1].trim();
      rows.push({ label, value, growth });
      continue;
    }

    // Fallback: keep as a single unlabeled row so we never drop facts
    rows.push({ label: "", value: chunk });
  }

  return { period, rows };
};

const EarningsCard: React.FC<{ ticker: string; text: string }> = ({ ticker, text }) => {
  const { period, rows } = parseEarningsBeats(text);
  const useStructured = rows.length >= 1 && rows.some((r) => r.label);

  return (
    <article
      style={{
        background: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: 14,
        padding: "14px 14px 12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span
          style={{
            fontFamily: FONT,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: ACCENT,
            background: ACCENT_SOFT,
            borderRadius: 6,
            padding: "4px 9px",
            lineHeight: 1.2,
          }}
        >
          {ticker}
        </span>
        {period ? (
          <span
            style={{
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 600,
              color: MUTED,
              letterSpacing: "0.02em",
            }}
          >
            {period}
          </span>
        ) : null}
      </div>

      {useStructured ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((row, i) => {
            const growthSign = row.growth ? signOf(row.growth) : "flat";
            return (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)",
                  gap: 8,
                  alignItems: "baseline",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  {row.label ? (
                    <div
                      style={{
                        fontFamily: FONT,
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: MUTED,
                        marginBottom: 2,
                      }}
                    >
                      {row.label}
                    </div>
                  ) : null}
                  <div
                    style={{
                      fontFamily: FONT,
                      fontSize: 15,
                      fontWeight: 600,
                      color: INK,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {row.value}
                  </div>
                </div>
                {row.growth ? (
                  <div
                    style={{
                      justifySelf: "end",
                      textAlign: "right",
                      fontFamily: FONT,
                      fontSize: 13,
                      fontWeight: 600,
                      color: colorForSign(growthSign),
                      background: bgForSign(growthSign),
                      borderRadius: 6,
                      padding: "4px 8px",
                      lineHeight: 1.3,
                    }}
                  >
                    {row.growth}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            fontFamily: FONT,
            fontSize: 15,
            lineHeight: 1.5,
            color: BODY,
          }}
        >
          {renderRich(text)}
        </div>
      )}
    </article>
  );
};

const SectionShell: React.FC<{
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}> = ({ eyebrow, title, children }) => (
  <section style={{ marginTop: 28 }}>
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: `2px solid ${INK}`,
      }}
    >
      <div>
        {eyebrow ? (
          <div
            style={{
              fontFamily: FONT,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: MUTED,
              marginBottom: 2,
            }}
          >
            {eyebrow}
          </div>
        ) : null}
        <h2
          style={{
            fontFamily: FONT,
            fontSize: 20,
            fontWeight: 600,
            color: INK,
            margin: 0,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </h2>
      </div>
    </div>
    {children}
  </section>
);

const FilingCard: React.FC<{
  ticker: string;
  text: string;
  featured?: boolean;
  index?: number;
}> = ({ ticker, text, featured, index }) => {
  const beats = splitBeats(text);
  return (
    <article
      style={{
        background: featured ? "#fff" : SURFACE,
        border: `1px solid ${featured ? "#c7d2fe" : BORDER}`,
        borderRadius: 14,
        padding: featured ? "16px 16px 14px" : "14px 14px 12px",
        boxShadow: featured ? "0 1px 2px rgba(15,23,42,0.04)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {typeof index === "number" && (
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 8,
              background: ACCENT_SOFT,
              color: ACCENT,
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {index}
          </span>
        )}
        <span
          style={{
            fontFamily: FONT,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: ACCENT,
            background: ACCENT_SOFT,
            borderRadius: 6,
            padding: "4px 9px",
            lineHeight: 1.2,
          }}
        >
          {ticker}
        </span>
      </div>
      <div
        style={{
          fontFamily: FONT,
          fontSize: 16,
          lineHeight: 1.55,
          color: BODY,
          letterSpacing: "-0.01em",
        }}
      >
        {beats.map((beat, i) => (
          <p
            key={i}
            style={{
              margin: i === 0 ? 0 : "8px 0 0",
              paddingLeft: beats.length > 1 ? 10 : 0,
              borderLeft: beats.length > 1 ? `2px solid ${BORDER}` : "none",
            }}
          >
            {renderRich(beat)}
          </p>
        ))}
      </div>
    </article>
  );
};

const IndexCard: React.FC<{
  name: string;
  last: string;
  change?: string;
  pct?: string;
  compact?: boolean;
}> = ({ name, last, change, pct, compact }) => {
  const sign = signOf(pct || change || "");
  return (
    <div
      style={{
        flex: compact ? "1 1 calc(50% - 6px)" : "1 1 150px",
        minWidth: compact ? 0 : 140,
        background: bgForSign(sign),
        border: `1px solid ${sign === "flat" ? BORDER : sign === "pos" ? "#a7f3d0" : "#fecaca"}`,
        borderRadius: 14,
        padding: compact ? "12px 12px" : "14px 14px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: MUTED,
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {name}
      </div>
      <div
        style={{
          fontFamily: FONT,
          fontSize: compact ? 18 : 22,
          fontWeight: 700,
          color: INK,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.03em",
          lineHeight: 1.1,
        }}
      >
        {last}
      </div>
      <div
        style={{
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: 700,
          color: colorForSign(sign),
          marginTop: 6,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatIndexMove(change, pct)}
      </div>
    </div>
  );
};

const MoverRow: React.FC<{
  symbol: string;
  ltp: string;
  pct?: string;
  tone: "pos" | "neg";
}> = ({ symbol, ltp, pct, tone }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr auto auto",
      gap: 10,
      alignItems: "center",
      padding: "12px 12px",
      borderTop: `1px solid ${tone === "pos" ? "#d1fae5" : "#fecaca"}`,
      fontFamily: FONT,
    }}
  >
    <span style={{ fontWeight: 700, color: INK, fontSize: 14, letterSpacing: "0.02em" }}>
      {symbol}
    </span>
    <span
      style={{
        color: MUTED,
        fontSize: 13,
        fontVariantNumeric: "tabular-nums",
        textAlign: "right",
      }}
    >
      ₹{ltp.replace(/^₹/, "")}
    </span>
    {pct && (
      <span
        style={{
          color: tone === "pos" ? POS : NEG,
          fontWeight: 700,
          fontSize: 14,
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
          minWidth: 58,
        }}
      >
        {pct}
      </span>
    )}
  </div>
);

const MarketPulse: React.FC<{ lines: string[] }> = ({ lines }) => {
  const { isMobile } = useMobile();
  let mode: "none" | "indices" | "gainers" | "losers" = "none";
  const indices: NonNullable<ReturnType<typeof parseIndexLine>>[] = [];
  const gainers: NonNullable<ReturnType<typeof parseMoverLine>>[] = [];
  const losers: NonNullable<ReturnType<typeof parseMoverLine>>[] = [];

  for (const raw of lines) {
    const t = raw.trim();
    if (!t) continue;
    const lower = stripBold(t).toLowerCase();
    if (lower.startsWith("indices")) {
      mode = "indices";
      continue;
    }
    if (lower.includes("top gainer")) {
      mode = "gainers";
      continue;
    }
    if (lower.includes("top loser")) {
      mode = "losers";
      continue;
    }
    if (lower.startsWith("as of")) continue;
    if (!t.startsWith("-") && !t.startsWith("*")) continue;
    if (mode === "indices") {
      const p = parseIndexLine(t);
      if (p) indices.push(p);
    } else if (mode === "gainers") {
      const p = parseMoverLine(t);
      if (p) gainers.push(p);
    } else if (mode === "losers") {
      const p = parseMoverLine(t);
      if (p) losers.push(p);
    }
  }

  return (
    <SectionShell eyebrow="" title="Market pulse">
      {indices.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {indices.map((ix) => (
            <IndexCard
              key={ix.name}
              name={ix.name}
              last={ix.last}
              change={ix.change}
              pct={ix.pct}
              compact={isMobile}
            />
          ))}
        </div>
      )}
      {(gainers.length > 0 || losers.length > 0) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 10,
          }}
        >
          {gainers.length > 0 && (
            <div
              style={{
                background: POS_BG,
                border: "1px solid #a7f3d0",
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontFamily: FONT,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: POS,
                  padding: "12px 12px 4px",
                }}
              >
                Top gainers
              </div>
              {gainers.map((g) => (
                <MoverRow key={g.symbol} symbol={g.symbol} ltp={g.ltp} pct={g.pct} tone="pos" />
              ))}
            </div>
          )}
          {losers.length > 0 && (
            <div
              style={{
                background: NEG_BG,
                border: "1px solid #fecaca",
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontFamily: FONT,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: NEG,
                  padding: "12px 12px 4px",
                }}
              >
                Top losers
              </div>
              {losers.map((l) => (
                <MoverRow key={l.symbol} symbol={l.symbol} ltp={l.ltp} pct={l.pct} tone="neg" />
              ))}
            </div>
          )}
        </div>
      )}
    </SectionShell>
  );
};

const Headlines: React.FC<{ lines: string[] }> = ({ lines }) => {
  const items = lines
    .map((l) => l.trim())
    .filter((l) => l.startsWith("-") || l.startsWith("*"))
    .map((l) => parseTickerBullet(l) || { ticker: "UPDATE", rest: l.replace(/^[-*]\s+/, "") });

  if (!items.length) return null;

  return (
    <SectionShell eyebrow="Must know" title="Today's headlines">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map((item, i) => (
          <FilingCard
            key={`${item.ticker}-${i}`}
            ticker={item.ticker}
            text={item.rest}
            featured
            index={items.length > 1 ? i + 1 : undefined}
          />
        ))}
      </div>
    </SectionShell>
  );
};

const ResultsTable: React.FC<{ lines: string[] }> = ({ lines }) => {
  const { isMobile } = useMobile();
  const rows: string[][] = [];
  const extraBullets: string[] = [];
  for (const raw of lines) {
    const t = raw.trim();
    if (t.startsWith("-") || t.startsWith("*")) {
      extraBullets.push(t);
      continue;
    }
    if (!t.includes("|") || isTableSep(t)) continue;
    rows.push(splitRow(t));
  }

  if (rows.length === 0) {
    const items = lines
      .map((l) => l.trim())
      .filter((l) => l.startsWith("-") || l.startsWith("*"))
      .map((l) => parseTickerBullet(l) || { ticker: "", rest: l.replace(/^[-*]\s+/, "") })
      .filter((i) => i.rest);

    if (!items.length) return null;

    return (
      <SectionShell eyebrow="Earnings" title="Results">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item, i) =>
            item.ticker ? (
              <EarningsCard key={`${item.ticker}-${i}`} ticker={item.ticker} text={item.rest} />
            ) : (
              <p
                key={i}
                style={{
                  fontFamily: FONT,
                  fontSize: 16,
                  lineHeight: 1.55,
                  color: BODY,
                  margin: 0,
                }}
              >
                {renderRich(item.rest)}
              </p>
            )
          )}
        </div>
      </SectionShell>
    );
  }

  const [header, ...body] = rows;

  // Mobile: stack each company as a card instead of a cramped table
  if (isMobile) {
    return (
      <SectionShell eyebrow="Earnings" title="Results">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {body.map((row, ri) => {
            const company = stripBold(row[0] || "");
            const cells = header.slice(1).map((h, i) => ({
              label: stripBold(h),
              value: stripBold(row[i + 1] || "—"),
            }));
            return (
              <div
                key={ri}
                style={{
                  background: "#fff",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 14,
                  padding: "14px 14px 10px",
                }}
              >
                <div
                  style={{
                    fontFamily: FONT,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: ACCENT,
                    marginBottom: 10,
                  }}
                >
                  {company}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  {cells.map((c) => {
                    return (
                      <div
                        key={c.label}
                        style={{
                          background: SURFACE,
                          borderRadius: 10,
                          padding: "8px 10px",
                        }}
                      >
                        <div
                          style={{
                            fontFamily: FONT,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: MUTED,
                            marginBottom: 2,
                          }}
                        >
                          {c.label}
                        </div>
                        <div
                          style={{
                            fontFamily: FONT,
                            fontSize: 14,
                            fontWeight: 700,
                            color: BODY,
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1.3,
                          }}
                        >
                          {c.value}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {extraBullets.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <FilingList eyebrow="" title="" lines={extraBullets} bare />
          </div>
        )}
      </SectionShell>
    );
  }

  return (
    <SectionShell eyebrow="Earnings" title="Results">
      <div
        style={{
          overflowX: "auto",
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          background: "#fff",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            fontFamily: FONT,
            fontSize: 14,
            lineHeight: "20px",
          }}
        >
          <thead>
            <tr style={{ background: SURFACE }}>
              {header.map((cell, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: i === 0 ? "left" : "right",
                    padding: "12px 14px",
                    borderBottom: `1px solid ${BORDER}`,
                    color: MUTED,
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stripBold(cell)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => {
                  const plain = stripBold(cell);
                  const isFirst = ci === 0;
                  return (
                    <td
                      key={ci}
                      style={{
                        padding: "12px 14px",
                        borderBottom: `1px solid ${BORDER}`,
                        textAlign: isFirst ? "left" : "right",
                        color: isFirst ? INK : BODY,
                        fontWeight: isFirst ? 700 : 400,
                        fontVariantNumeric: "tabular-nums",
                        verticalAlign: "top",
                      }}
                    >
                      {plain}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {extraBullets.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <FilingList eyebrow="" title="" lines={extraBullets} bare />
        </div>
      )}
    </SectionShell>
  );
};

const FilingList: React.FC<{
  eyebrow: string;
  title: string;
  lines: string[];
  bare?: boolean;
}> = ({ eyebrow, title, lines, bare }) => {
  const items = lines
    .map((l) => l.trim())
    .filter((l) => l.startsWith("-") || l.startsWith("*"))
    .map((l) => parseTickerBullet(l) || { ticker: "", rest: l.replace(/^[-*]\s+/, "") })
    .filter((i) => i.rest);

  if (!items.length) return null;

  const list = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((item, i) =>
        item.ticker ? (
          <FilingCard key={`${item.ticker}-${i}`} ticker={item.ticker} text={item.rest} />
        ) : (
          <p
            key={i}
            style={{
              fontFamily: FONT,
              fontSize: 16,
              lineHeight: 1.55,
              color: BODY,
              margin: 0,
            }}
          >
            {renderRich(item.rest)}
          </p>
        )
      )}
    </div>
  );

  if (bare || !title) return list;

  return (
    <SectionShell eyebrow={eyebrow} title={title}>
      {list}
    </SectionShell>
  );
};

export const WrapDigest: React.FC<{ content: string }> = ({ content }) => {
  const { sections } = parseSections(content);

  return (
    <article style={{ width: "100%" }}>
      {sections.map((sec, i) => {
        if (sec.key === "footer") return null;
        if (sec.key === "market") return <MarketPulse key={i} lines={sec.lines} />;
        if (sec.key === "headlines") return <Headlines key={i} lines={sec.lines} />;
        if (sec.key === "results") return <ResultsTable key={i} lines={sec.lines} />;
        if (sec.key === "guidance")
          return <FilingList key={i} eyebrow="Outlook" title="Guidance" lines={sec.lines} />;
        if (sec.key === "capital")
          return (
            <FilingList key={i} eyebrow="Deals" title="Capital & control" lines={sec.lines} />
          );
        if (sec.key === "orders")
          return <FilingList key={i} eyebrow="Business" title="Orders" lines={sec.lines} />;
        if (sec.key === "ratings")
          return (
            <FilingList key={i} eyebrow="Credit" title="Credit & ratings" lines={sec.lines} />
          );
        if (sec.key === "calendar") return null;
        return <FilingList key={i} eyebrow="Update" title={sec.title} lines={sec.lines} />;
      })}
    </article>
  );
};
